/* eslint-disable import/order, import/no-extraneous-dependencies, @typescript-eslint/no-unused-vars */
// 1. The import order of macros matter and they must be kept in this order
// 2. Since macros are transpiled out during build, it is okay for them
//   to be imported even when they are not used.
// -- color must always be first -- //
import color from '@haaretz/l-color.macro';
// ---
import fontStack from '@haaretz/l-font-stack.macro';
import radius from '@haaretz/l-radius.macro';
import space from '@haaretz/l-space.macro';
import zIndex from '@haaretz/l-z-index.macro';
// --- These return objects and must be spread or used inside `merge` --- //
import border from '@haaretz/l-border.macro';
import shadow from '@haaretz/l-shadow.macro';
import typesetter from '@haaretz/l-type.macro';
// --- These must come last --- //
import fork from '@haaretz/l-fork.macro';
import mq from '@haaretz/l-mq.macro';
import merge from '@haaretz/l-merge.macro';
/* eslint-enable import/order, import/no-extraneous-dependencies, @typescript-eslint/no-unused-vars */

import * as React from 'react';
import s9 from 'style9';

import type { StepDataFragment } from '@haaretz/s-fragments/StepData';
import type { StyleExtend, InlineStyles } from '@haaretz/s-types';

// `c` is short for `classNames`
const c = s9.create({
  base: {
    color: color('neutral1200'),
    fontWeight: fork({
      default: 300,
      hdc: 400,
    }),
    fontFamily: fork({
      default: fontStack('primary'),
      hdc: fontStack('secondary'),
    }),
    textAlign: 'center',
    ...merge(
      fork({
        default: { ...typesetter(2) },
        hdc: { ...typesetter(0) },
      }),
      mq({
        from: 'm',
        until: 'xl',
        value: {
          ...fork({
            default: { ...typesetter(2) },
            hdc: { ...typesetter(1) },
          }),
        },
      }),
      mq({
        from: 'xl',
        until: 'xxl',
        value: {
          ...fork({
            default: { ...typesetter(3) },
            hdc: { ...typesetter(1) },
          }),
        },
      }),
      mq({
        from: 'xxl',
        value: {
          ...fork({
            default: { ...typesetter(2) },
            hdc: { ...typesetter(1) },
          }),
        },
      })
    ),
  },
});

export interface PromotionsInstructionsProps {
  /** The Children to be rendered inside `<PromotionsInstructions>` */
  instructions?: StepDataFragment['instructions'];
  /**
   * CSS declarations to be set as inline `style` on the
   * html element.
   *
   * By setting values of CSS Custom Properties based on
   * props or state in the consuming component (where
   * the value of `inlineStyle` is passed), `inlineStyle`
   * can be used as an API contract for setting dynamic
   * values to styles created with `style9.create()`:
   *
   * @example
   * ```ts
   * import s9 from 'style9';
   * const { styleExtend, } = s9.create({
   *   styleExtend: {
   *     color: 'var(--color-based-on-prop)',
   *   },
   * });
   *
   * function MyButton(props) {
   *   const inlineStyle = {
   *     '--color-based-on-prop': props.color,
   *   },
   *
   *   return (
   *    <Button
   *      styleExtend={[ styleExtend, ]}
   *      inlineStyle={inlineStyle}
   *    />
   *   );
   * }
   * ```
   */
  inlineStyle?: InlineStyles;
  /**
   * An array of `Style`s created by `style9.create()`.
   * WARNING: **_do not_** pass simple CSS-in-JS object.
   * The items in the array must be created with Style9's
   * `create` function.
   * The array can also hold falsy values to assist with
   * conditional inclusion of `Style`s:
   *
   * @example
   * ```ts
   * const { foo, bar, } = s9.create({ foo: { ... }, bar: { ... }, });
   * <Button styleExtend={[ someCondition && foo, bar, ]} />
   * ```
   */
  styleExtend?: StyleExtend;
}

export default function PromotionsInstructions({
  instructions,
  inlineStyle,
  styleExtend = [],
}: PromotionsInstructionsProps) {
  return instructions ? (
    <p
      className={s9(c.base, ...styleExtend)}
      id="pp-page-instructions"
      style={inlineStyle}
      data-testid="pp-instructions"
    >
      {instructions}
    </p>
  ) : null;
}
